import React from 'react';
import { FileText } from 'lucide-react';

const Terms = () => {
  return (
    <div className="py-20 animate-fade-in">
      <div className="container-custom">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex p-4 glass-card rounded-2xl mb-6">
              <FileText className="w-12 h-12 text-primary" />
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold mb-4">
              Terms of <span className="gradient-text">Service</span>
            </h1>
            <p className="text-muted-foreground">
              Last Updated: January 1, 2024
            </p>
          </div>

          {/* Content */}
          <div className="glass-card p-8 lg:p-12 space-y-8">
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-bold mb-4">1. Acceptance of Terms</h2>
              <p className="text-muted-foreground leading-relaxed">
                By accessing and using NoTrackTools (“the Service”), you accept and agree to be
                bound by these Terms of Service. If you do not agree to these terms, please do
                not use the Service.
              </p>
            </section>

            {/* Description of Service */}
            <section>
              <h2 className="text-2xl font-bold mb-4">2. Description of Service</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                NoTrackTools provides privacy-focused security tools that operate entirely within
                your web browser. All processing is client-side, and we do not collect, store, or
                transmit your data.
              </p>
            </section>

            {/* User Responsibilities */}
            <section>
              <h2 className="text-2xl font-bold mb-4">3. User Responsibilities</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                You agree to:
              </p>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  Use the Service only for lawful purposes
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  Not attempt to circumvent any security features
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  Not use the Service to encrypt illegal content
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  Not reverse engineer or attempt to extract source code
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  Maintain the confidentiality of your passwords and encryption keys
                </li>
              </ul>
            </section>

            {/* No Warranty */}
            <section>
              <h2 className="text-2xl font-bold mb-4">4. Disclaimer of Warranties</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                THE SERVICE IS PROVIDED “AS IS” AND “AS AVAILABLE” WITHOUT ANY WARRANTIES OF ANY
                KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
              </p>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  Warranties of merchantability or fitness for a particular purpose
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  Warranties that the Service will be uninterrupted or error-free
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  Warranties regarding the accuracy or reliability of results
                </li>
              </ul>
            </section>

            {/* Limitation of Liability */}
            <section>
              <h2 className="text-2xl font-bold mb-4">5. Limitation of Liability</h2>
              <p className="text-muted-foreground leading-relaxed">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, NOTRACKTOOLS SHALL NOT BE LIABLE FOR ANY
                INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF
                PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA,
                USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
              </p>
            </section>

            {/* Data Loss */}
            <section>
              <h2 className="text-2xl font-bold mb-4">6. Data Loss and Recovery</h2>
              <p className="text-muted-foreground leading-relaxed">
                We are not responsible for any data loss. Since all processing happens locally in
                your browser, you are responsible for saving and backing up your outputs. Lost
                passwords or encryption keys cannot be recovered.
              </p>
            </section>

            {/* Intellectual Property */}
            <section>
              <h2 className="text-2xl font-bold mb-4">7. Intellectual Property</h2>
              <p className="text-muted-foreground leading-relaxed">
                The Service and its original content, features, and functionality are owned by
                NoTrackTools and are protected by international copyright, trademark, and other
                intellectual property laws.
              </p>
            </section>

            {/* Third-Party Services */}
            <section>
              <h2 className="text-2xl font-bold mb-4">8. Third-Party Services</h2>
              <p className="text-muted-foreground leading-relaxed">
                Our Service may display advertisements through Google AdSense. We are not
                responsible for the content or practices of third-party advertisers. Please refer
                to our Privacy Policy for more information.
              </p>
            </section>

            {/* Modifications */}
            <section>
              <h2 className="text-2xl font-bold mb-4">9. Modifications to Service</h2>
              <p className="text-muted-foreground leading-relaxed">
                We reserve the right to modify, suspend, or discontinue the Service at any time
                without notice. We will not be liable to you or any third party for any
                modification, suspension, or discontinuation.
              </p>
            </section>

            {/* Governing Law */}
            <section>
              <h2 className="text-2xl font-bold mb-4">10. Governing Law</h2>
              <p className="text-muted-foreground leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of the
                jurisdiction in which NoTrackTools operates, without regard to its conflict of law
                provisions.
              </p>
            </section>

            {/* Changes to Terms */}
            <section>
              <h2 className="text-2xl font-bold mb-4">11. Changes to Terms</h2>
              <p className="text-muted-foreground leading-relaxed">
                We reserve the right to update these Terms at any time. We will notify users of
                any material changes by posting the new Terms on this page with an updated “Last
                Updated” date.
              </p>
            </section>

            {/* Contact */}
            <section>
              <h2 className="text-2xl font-bold mb-4">12. Contact Information</h2>
              <p className="text-muted-foreground leading-relaxed">
                If you have any questions about these Terms, please contact us at:
                <a
                  href="mailto:praneethartha381@gmail.com"
                  className="block mt-2 text-primary hover:underline"
                >
                  praneethartha381@gmail.com
                </a>
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms;
